import { surfacePalette, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import { PAVING_MATERIALS, ROOF_SHAPES, type PavingMaterial, type RoofShape } from '@/site/SiteObject';
import { SitePlanFormatError, type PlanPlanting } from './SitePlan';

/**
 * The staged reconstruction pipeline: what each stage produces, and how to read it back.
 *
 * The single-call `SitePlan` asked one model, once, to do every judgement a reconstruction needs.
 * That works and it is cheap, but it is opaque: when a site comes out wrong there is no way to see
 * *which* judgement failed, because they all arrived in one object. This module replaces that with
 * five stages that run in the order the work actually depends on, each producing an artifact that
 * can be read, diffed, and re-run on its own:
 *
 * | # | Stage | Produces | Depends on |
 * | - | ----- | -------- | ---------- |
 * | 1 | `segment` | {@link SemanticMap} — what is in the photograph, as labelled regions | the photograph |
 * | 2 | `terrain` | {@link TerrainArtifact} — the ground's shape, as control points | 1 |
 * | 3 | `surfaces` | {@link SurfaceArtifact} — blacktop / water / grass / gravel / mulch, as polygons | 1, 2 |
 * | 4 | `flora` | {@link FloraArtifact} — planting masses and species | 1, 2, 3 |
 * | 5 | `structures` | {@link StructureArtifact} — buildings and paving, as polygon massing | 1, 2, 3 |
 *
 * Two conventions hold the chain together, and both exist so a later stage can be checked against
 * an earlier one rather than merely trusted:
 *
 * - **Stage 1 works in normalized image space** — every polygon is `[u, v]` in `[0, 1]`, origin at
 *   the top-left of the reference image. That is the only space available before a scale exists, and
 *   it is what lets the inspector draw the segmentation straight over the photograph.
 * - **Stages 2–5 work in world metres**, on the frame stage 1 fixed: origin at the site centre,
 *   `+X` image-right, `+Z` image-down, spanning `±width/2` by `±length/2`. Each later region carries
 *   the `regionId` it came from, so "why is there blacktop here" has an answer.
 *
 * Parsing is forgiving about values and strict about structure, exactly as `parseSitePlan` is: a
 * model will return a confidence of 3 or a stray null, and clamping those beats discarding a paid
 * call, but a missing `regions` array is a stage that did not do its job and must say so.
 */

export const SITE_PIPELINE_SCHEMA_VERSION = 1;

export type PipelineStageId = 'segment' | 'terrain' | 'surfaces' | 'flora' | 'structures';

/** Stage order. The runner walks this, and every "re-run from here" is a slice of it. */
export const PIPELINE_STAGE_IDS: readonly PipelineStageId[] = [
  'segment',
  'terrain',
  'surfaces',
  'flora',
  'structures',
];

export interface PipelineStageMeta {
  id: PipelineStageId;
  /** Shown as the stage's heading in the inspector. */
  title: string;
  /** One line describing what this stage decides. */
  summary: string;
}

export const PIPELINE_STAGES: readonly PipelineStageMeta[] = [
  {
    id: 'segment',
    title: 'Semantic identification',
    summary: 'Names every region in the photograph and fixes the scale everything else is measured against.',
  },
  {
    id: 'terrain',
    title: 'Terrain',
    summary: 'Reads the ground’s shape as elevation control points, which the compiler sculpts into the heightfield.',
  },
  {
    id: 'surfaces',
    title: 'Ground surfaces',
    summary: 'Turns the paved, watered and planted regions into painted ground — blacktop, water, gravel, mulch, lawn.',
  },
  {
    id: 'flora',
    title: 'Planting',
    summary: 'Decomposes the vegetation into masses, assigns catalog species, and describes how each is distributed.',
  },
  {
    id: 'structures',
    title: 'Structures',
    summary: 'Builds houses, sheds, decks and pools as polygon footprints and stacked masses.',
  },
];

export function stageMeta(id: PipelineStageId): PipelineStageMeta {
  const meta = PIPELINE_STAGES.find((stage) => stage.id === id);
  if (!meta) throw new Error(`Unknown pipeline stage: ${id}`);
  return meta;
}

/* ------------------------------- Stage 1: segment ------------------------------- */

/**
 * What a region *is*, in the tool's own vocabulary rather than the photograph's.
 *
 * Deliberately coarse. The label carries the detail ("cracked asphalt driveway", "mature sugar
 * maple"); the category carries the routing — which later stage is responsible for the region and
 * what it can become. A model asked for both gives a far better label than it gives a taxonomy.
 */
export type SemanticCategory =
  | 'building'
  | 'paving'
  | 'water'
  | 'lawn'
  | 'planting-bed'
  | 'tree'
  | 'shrub'
  | 'bare-earth'
  | 'vehicle'
  | 'unmodelled';

export const SEMANTIC_CATEGORIES: readonly SemanticCategory[] = [
  'building',
  'paving',
  'water',
  'lawn',
  'planting-bed',
  'tree',
  'shrub',
  'bare-earth',
  'vehicle',
  'unmodelled',
];

export interface SemanticRegion {
  id: string;
  /** Free text, as specific as the photograph supports: "asphalt driveway", "detached garage". */
  label: string;
  category: SemanticCategory;
  /** Outline in normalized image space, `[u, v]` in `[0, 1]`, origin top-left. */
  polygon: [number, number][];
  /** The model's own confidence, `0..1`. Surfaced in the inspector rather than acted on. */
  confidence: number;
  notes?: string;
}

/** What the model measured against to fix metres per pixel. Recorded so the scale is auditable. */
export interface ScaleAnchor {
  /** The object used — "shed door", "sedan", "standard doorway". */
  description: string;
  /** Its extent as a fraction of image width, `0..1`. */
  imageFraction: number;
  /** Its real size in metres. */
  meters: number;
}

export interface SiteObservations {
  dayOfYear: number;
  hour: number;
  minute: number;
  latitudeDegrees: number;
  weather?: string;
  /** Read off the shadows, before solving for a time. Kept so the solve can be checked. */
  sunAzimuthDegrees?: number;
  sunElevationDegrees?: number;
}

export interface SemanticMap {
  scale: ScaleAnchor;
  site: {
    widthMeters: number;
    lengthMeters: number;
    /** Which way the site faces, in words. The sun model ignores `northDegrees`, so this is prose. */
    orientation: string;
  };
  observations: SiteObservations;
  regions: SemanticRegion[];
  /** Things visible in the photograph this tool cannot represent — fences, furniture, cars. */
  unmodelled: string[];
  notes: string;
}

/* ------------------------------- Stage 2: terrain ------------------------------- */

/**
 * One elevation reading, in world metres. The compiler blends these into the procedural base with
 * inverse-distance weighting, so a plan can raise a berm or cut a swale without authoring a grid.
 */
export interface TerrainControlPoint {
  x: number;
  z: number;
  elevationMeters: number;
  /** How far this reading's influence reaches. Larger is a landform, smaller is a mound. */
  radiusMeters: number;
  notes?: string;
}

/**
 * Ground that was **measured**, resampled onto the site's own axes.
 *
 * A regular lattice in site metres, row-major from `(originX, originZ)`. It exists because the
 * control-point path — twenty-odd readings blended into procedural noise — is the right shape for a
 * *guess* and destructive for a *survey*: the compiler's lattice is 145 × 145, and reconstructing
 * 21,000 vertices from 24 points erases every swale, crown and retaining bank the DEM actually
 * recorded. When this is present the compiler resamples it directly and skips the noise entirely.
 *
 * Already on the site's axes rather than the source's, because the frame's projection — rotation,
 * quad rectification and all — is applied once on the way in. The compiler then needs nothing but a
 * bilinear lookup, and knows nothing about latitude.
 *
 * `null` marks a sample the source had no reading for; the compiler leaves those at the site datum
 * and reports how much of the site that was, because a flat plate where a lake was is legible and a
 * silently interpolated one is not.
 */
export interface MeasuredGround {
  /** Site-metre position of the sample at column 0 / row 0. */
  originX: number;
  originZ: number;
  spacingXMeters: number;
  spacingZMeters: number;
  cols: number;
  rows: number;
  /** Row-major, `cols * rows`, metres relative to the site datum. */
  heights: (number | null)[];
  /** Where it came from — a file name, or a service. Quoted in the project description. */
  label: string;
}

export interface TerrainArtifact {
  /** Amplitude of the procedural noise underneath the control points. */
  elevationAmplitude: number;
  roughness: number;
  controlPoints: TerrainControlPoint[];
  notes: string;
  /**
   * Measured ground, when there is any. Authoritative: the compiler bakes this and ignores both the
   * procedural base and {@link controlPoints}, which survive alongside it as the *description* of
   * the landform that stages 3–5 read. That split is deliberate — control points were never wrong as
   * a summary, only as a reconstruction.
   */
  measured?: MeasuredGround;
}

/* ------------------------------ Stage 3: surfaces ------------------------------ */

export interface SurfaceRegionPlan {
  id: string;
  /** The `segment` region this came from, when it came from one. */
  regionId?: string;
  material: SurfaceLayerId;
  /** Outline in world metres, `[x, z]`. */
  polygon: [number, number][];
  /** How far the edge fades, in metres. A drive edge is crisp; a lawn/bed margin is not. */
  featherMeters: number;
  notes?: string;
}

/**
 * A ground material the stage needs and the palette does not have.
 *
 * The reconstruction used to be limited to five materials, which meant every photograph was
 * compressed into grass / mulch / water / blacktop / gravel on the way in. That is fine for a lawn
 * and a tarmac drive and quietly wrong for everything else: a red decomposed-granite path, a
 * bluestone terrace and a bark bed all arrived as "gravel", and the error was baked into the site
 * before anyone saw it.
 *
 * So the stage may declare materials instead of rounding to the nearest one it has. Each declaration
 * is a *request*, not a material — the runner forges it against the web, the palette assigns the
 * real id, and the regions that named it are rewritten to point at what was actually made.
 */
export interface RequestedMaterial {
  /** The id this stage's regions use to refer to it. Rewritten once the material exists. */
  id: string;
  name: string;
  /** What to research: "crushed red lava rock, 20 mm chips, matte". */
  request: string;
  /** Where it goes on this site, which sharpens the research. */
  context?: string;
}

export interface SurfaceArtifact {
  regions: SurfaceRegionPlan[];
  /** Materials this stage needs that the palette does not have. Empty on most sites. */
  materials?: RequestedMaterial[];
  notes: string;
}

/* -------------------------------- Stage 4: flora -------------------------------- */

export interface FloraArtifact {
  plantings: PlanPlanting[];
  notes: string;
}

/* ------------------------------ Stage 5: structures ------------------------------ */

/** One extruded volume of a building, outlined in **world** metres. */
export interface PlanMass {
  name?: string;
  /** Outline in world metres, `[x, z]`. */
  polygon: [number, number][];
  heightMeters: number;
  roof: RoofShape;
  roofRiseMeters: number;
  baseMeters?: number;
  ridgeCrosswise?: boolean;
}

/**
 * A building or paved surface as the pipeline describes it.
 *
 * Shape is given as a **world-space polygon**, not as a centre plus extents plus an angle. That is
 * the whole point of the polygon work: a model that has just traced an outline in the image can map
 * those vertices into metres directly, whereas "centre, width, depth, rotation" asks it to fit a
 * rectangle it cannot see and get a rotation sign right. The compiler derives the object's origin
 * and local frame from the polygon.
 */
export interface PlanStructure {
  id: string;
  kind: 'building' | 'paving';
  name: string;
  regionId?: string;
  /** Outline in world metres. Optional only so a plain rectangle can still be expressed. */
  polygon?: [number, number][];
  /** Several volumes under one record — house plus garage plus porch. Buildings only. */
  masses?: PlanMass[];
  /** Fallback shape when no polygon is given. */
  x?: number;
  z?: number;
  rotationDegrees?: number;
  widthMeters?: number;
  depthMeters?: number;
  heightMeters?: number;
  roof?: RoofShape;
  roofRiseMeters?: number;
  wallColor?: string;
  roofColor?: string;
  paving?: PavingMaterial;
  clearanceMeters?: number;
  /** Lifts this object onto another's datum — a pool inside its terrace. */
  sitsOn?: string;
  notes?: string;
}

export interface StructureArtifact {
  structures: PlanStructure[];
  notes: string;
}

/** Every artifact, keyed by the stage that produced it. Partial until the run completes. */
export interface PipelineArtifacts {
  segment: SemanticMap;
  terrain: TerrainArtifact;
  surfaces: SurfaceArtifact;
  flora: FloraArtifact;
  structures: StructureArtifact;
}

/* --------------------------------- Parsing --------------------------------- */

export function parseStageArtifact<K extends PipelineStageId>(
  stage: K,
  raw: unknown,
): PipelineArtifacts[K] {
  switch (stage) {
    case 'segment':
      return parseSemanticMap(raw) as PipelineArtifacts[K];
    case 'terrain':
      return parseTerrainArtifact(raw) as PipelineArtifacts[K];
    case 'surfaces':
      return parseSurfaceArtifact(raw) as PipelineArtifacts[K];
    case 'flora':
      return parseFloraArtifact(raw) as PipelineArtifacts[K];
    case 'structures':
      return parseStructureArtifact(raw) as PipelineArtifacts[K];
    default:
      throw new SitePlanFormatError(`Unknown pipeline stage: ${String(stage)}`);
  }
}

export function parseSemanticMap(raw: unknown): SemanticMap {
  const data = asRecord(raw, 'segment');
  const scale = asRecord(data.scale ?? {}, 'segment.scale');
  const site = asRecord(data.site ?? {}, 'segment.site');
  const observations = asRecord(data.observations ?? {}, 'segment.observations');

  const map: SemanticMap = {
    scale: {
      description: text(scale.description) ?? 'unstated',
      imageFraction: clamp(number(scale.imageFraction, 0.1), 0.001, 1),
      meters: clamp(number(scale.meters, 1), 0.01, 200),
    },
    site: {
      widthMeters: clamp(number(site.widthMeters, 40), 5, 200),
      lengthMeters: clamp(number(site.lengthMeters, 40), 5, 200),
      orientation: text(site.orientation) ?? 'unstated',
    },
    observations: {
      dayOfYear: clamp(Math.trunc(number(observations.dayOfYear, 172)), 1, 365),
      hour: clamp(Math.trunc(number(observations.hour, 12)), 0, 23),
      minute: clamp(Math.trunc(number(observations.minute, 0)), 0, 59),
      latitudeDegrees: clamp(number(observations.latitudeDegrees, 45), -90, 90),
    },
    regions: asArray(data.regions, 'segment.regions').map(parseSemanticRegion),
    unmodelled: asArray(data.unmodelled ?? [], 'segment.unmodelled')
      .map((entry) => text(entry))
      .filter((entry): entry is string => entry !== undefined),
    notes: text(data.notes) ?? '',
  };

  const weather = text(observations.weather);
  if (weather) map.observations.weather = weather;
  if (observations.sunAzimuthDegrees !== undefined) {
    map.observations.sunAzimuthDegrees = wrap(number(observations.sunAzimuthDegrees, 0), 360);
  }
  if (observations.sunElevationDegrees !== undefined) {
    map.observations.sunElevationDegrees = clamp(number(observations.sunElevationDegrees, 45), -90, 90);
  }

  return map;
}

function parseSemanticRegion(raw: unknown, index: number): SemanticRegion {
  const where = `segment.regions[${index}]`;
  const data = asRecord(raw, where);

  const region: SemanticRegion = {
    id: text(data.id) ?? `region-${index + 1}`,
    label: text(data.label) ?? 'unlabelled',
    category: SEMANTIC_CATEGORIES.includes(data.category as SemanticCategory)
      ? (data.category as SemanticCategory)
      : 'unmodelled',
    // Clamped to the image: a vertex at 1.4 is a region the model ran off the edge, and cropping it
    // to the frame is a better reading than either discarding the region or trusting the number.
    polygon: parsePolygon(data.polygon, 0, 1),
    confidence: clamp(number(data.confidence, 0.5), 0, 1),
  };
  const notes = text(data.notes);
  if (notes) region.notes = notes;
  return region;
}

export function parseTerrainArtifact(raw: unknown): TerrainArtifact {
  const data = asRecord(raw, 'terrain');
  const measured = parseMeasuredGround(data.measured);
  return {
    ...(measured ? { measured } : {}),
    elevationAmplitude: clamp(number(data.elevationAmplitude, 0.5), 0, 20),
    roughness: clamp(number(data.roughness, 0.25), 0, 1),
    controlPoints: asArray(data.controlPoints ?? [], 'terrain.controlPoints').map(
      (entry, index) => {
        const point = asRecord(entry, `terrain.controlPoints[${index}]`);
        const out: TerrainControlPoint = {
          x: number(point.x, 0),
          z: number(point.z, 0),
          elevationMeters: clamp(number(point.elevationMeters, 0), -50, 50),
          radiusMeters: clamp(number(point.radiusMeters, 6), 0.5, 200),
        };
        const notes = text(point.notes);
        if (notes) out.notes = notes;
        return out;
      },
    ),
    notes: text(data.notes) ?? '',
  };
}

/** How many samples a lattice may carry before it is certainly not something a model authored. */
const MAX_MEASURED_SAMPLES = 262144;

/**
 * Measured ground, or `null`.
 *
 * `null` rather than a throw, and strict rather than forgiving, because this field is normally
 * *supplied* — it arrives from an import that built it correctly — and the only way a malformed one
 * reaches here is a model inventing it. A lattice whose length disagrees with its own `cols × rows`
 * would bake garbage into the terrain silently, so it is dropped and the control points, which are
 * validated separately, carry the stage instead.
 */
function parseMeasuredGround(raw: unknown): MeasuredGround | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;

  const cols = Math.trunc(number(data.cols, 0));
  const rows = Math.trunc(number(data.rows, 0));
  const heights = data.heights;
  if (cols < 2 || rows < 2 || cols * rows > MAX_MEASURED_SAMPLES) return undefined;
  if (!Array.isArray(heights) || heights.length !== cols * rows) return undefined;

  const spacingXMeters = number(data.spacingXMeters, 0);
  const spacingZMeters = number(data.spacingZMeters, 0);
  if (!(spacingXMeters > 0) || !(spacingZMeters > 0)) return undefined;

  return {
    originX: number(data.originX, 0),
    originZ: number(data.originZ, 0),
    spacingXMeters,
    spacingZMeters,
    cols,
    rows,
    // Anything unreadable becomes a hole rather than a zero: a zero is a claim about the ground and
    // a hole is an admission, and the compiler reports the difference.
    heights: heights.map((value) => {
      // `Number(null)` is 0, and a hole silently becoming a datum reading is exactly the confusion
      // this field exists to prevent.
      if (value === null || value === undefined || value === '') return null;
      const height = Number(value);
      return Number.isFinite(height) ? height : null;
    }),
    label: text(data.label) ?? 'measured ground',
  };
}

/**
 * An artifact with its bulk elided, for anywhere it is *read* rather than used.
 *
 * A measured lattice is tens of thousands of numbers. Two places would otherwise print all of them:
 * the inspector's artifact tab, where it buries every field that matters, and — far worse — the
 * prior-artifact block of every later stage's prompt, where it would cost more tokens than the rest
 * of the run put together and tell the model nothing it can act on. What stages 3–5 need from stage
 * 2 is the landform, and that is what the control points are for.
 */
export function artifactForDisplay<T>(artifact: T): T {
  const measured = (artifact as { measured?: MeasuredGround } | null)?.measured;
  if (!measured) return artifact;

  const { heights, ...rest } = measured;
  const sampled = heights.reduce((total: number, height) => total + (height === null ? 0 : 1), 0);
  return {
    ...artifact,
    measured: {
      ...rest,
      heights: `<${sampled.toLocaleString()} of ${heights.length.toLocaleString()} samples measured; not listed>`,
    },
  };
}

/** Materials one stage may ask to have made. Bounded so a confused reply cannot spend real money. */
export const MAX_REQUESTED_MATERIALS = 6;

export function parseSurfaceArtifact(raw: unknown): SurfaceArtifact {
  const data = asRecord(raw, 'surfaces');
  const materials = parseRequestedMaterials(data.materials);
  // A region may name a material that exists *or* one this stage has just asked for. Anything else
  // is a hallucinated id, and painting it would silently put the wrong material on the ground.
  const declared = new Set(materials.map((material) => material.id));
  const regions: SurfaceRegionPlan[] = [];

  for (const [index, entry] of asArray(data.regions ?? [], 'surfaces.regions').entries()) {
    const where = `surfaces.regions[${index}]`;
    const region = asRecord(entry, where);
    const material = text(region.material);
    if (!material || !(surfacePalette.has(material) || declared.has(material))) continue;

    const polygon = parsePolygon(region.polygon, -1000, 1000);
    if (polygon.length < 3) continue;

    const plan: SurfaceRegionPlan = {
      id: text(region.id) ?? `surface-${index + 1}`,
      material,
      polygon,
      featherMeters: clamp(number(region.featherMeters, 0.3), 0, 20),
    };
    const regionId = text(region.regionId);
    if (regionId) plan.regionId = regionId;
    const notes = text(region.notes);
    if (notes) plan.notes = notes;
    regions.push(plan);
  }

  // Only materials something actually paints with. A stage that asked for a material and then never
  // used it has changed its mind, and forging it would be a paid call for nothing.
  const used = new Set(regions.map((region) => region.material));
  const needed = materials.filter((material) => used.has(material.id));

  return {
    regions,
    ...(needed.length > 0 ? { materials: needed } : {}),
    notes: text(data.notes) ?? '',
  };
}

function parseRequestedMaterials(raw: unknown): RequestedMaterial[] {
  if (!Array.isArray(raw)) return [];
  const out: RequestedMaterial[] = [];
  const seen = new Set<string>();

  for (const entry of raw) {
    if (out.length >= MAX_REQUESTED_MATERIALS) break;
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const id = text(record.id);
    if (!id || seen.has(id)) continue;
    // Never shadow a material that already exists: the region would then be ambiguous, and forging
    // a second "gravel" is a paid call to duplicate something already in the palette.
    if (surfacePalette.has(id)) continue;

    const name = text(record.name) ?? id;
    const context = text(record.context);
    seen.add(id);
    out.push({
      id,
      name,
      request: text(record.request) ?? name,
      ...(context ? { context } : {}),
    });
  }
  return out;
}

export function parseFloraArtifact(raw: unknown): FloraArtifact {
  const data = asRecord(raw, 'flora');
  // Planting masses are unchanged from the single-call plan, so they reuse its reader rather than
  // acquiring a second, subtly different one.
  const plantings = asArray(data.plantings ?? [], 'flora.plantings').map((entry, index) =>
    parsePlantingEntry(entry, index),
  );
  return { plantings, notes: text(data.notes) ?? '' };
}

export function parseStructureArtifact(raw: unknown): StructureArtifact {
  const data = asRecord(raw, 'structures');
  const structures = asArray(data.structures ?? [], 'structures.structures').map(
    (entry, index) => parseStructure(entry, index),
  );
  return { structures, notes: text(data.notes) ?? '' };
}

function parseStructure(raw: unknown, index: number): PlanStructure {
  const where = `structures.structures[${index}]`;
  const data = asRecord(raw, where);
  const kind = data.kind === 'paving' ? 'paving' : 'building';

  const structure: PlanStructure = {
    id: text(data.id) ?? `structure-${index + 1}`,
    kind,
    name: text(data.name) ?? (kind === 'building' ? 'Building' : 'Paving'),
  };

  const polygon = parsePolygon(data.polygon, -1000, 1000);
  if (polygon.length >= 3) structure.polygon = polygon;

  if (Array.isArray(data.masses)) {
    const masses: PlanMass[] = [];
    for (const [i, entry] of data.masses.entries()) {
      const mass = asRecord(entry, `${where}.masses[${i}]`);
      const ring = parsePolygon(mass.polygon, -1000, 1000);
      if (ring.length < 3) continue;
      const out: PlanMass = {
        polygon: ring,
        heightMeters: clamp(number(mass.heightMeters, 2.6), 0, 60),
        roof: ROOF_SHAPES.includes(mass.roof as RoofShape) ? (mass.roof as RoofShape) : 'gable',
        roofRiseMeters: clamp(number(mass.roofRiseMeters, 1.2), 0, 30),
      };
      const name = text(mass.name);
      if (name) out.name = name;
      if (mass.baseMeters !== undefined) out.baseMeters = clamp(number(mass.baseMeters, 0), -20, 20);
      if (mass.ridgeCrosswise === true) out.ridgeCrosswise = true;
      masses.push(out);
    }
    if (masses.length > 0 && kind === 'building') structure.masses = masses;
  }

  // Rectangle fallback, for a shed that genuinely is a box.
  if (data.x !== undefined) structure.x = number(data.x, 0);
  if (data.z !== undefined) structure.z = number(data.z, 0);
  if (data.rotationDegrees !== undefined) {
    structure.rotationDegrees = number(data.rotationDegrees, 0);
  }
  if (data.widthMeters !== undefined) {
    structure.widthMeters = clamp(number(data.widthMeters, 4), 0.2, 200);
  }
  if (data.depthMeters !== undefined) {
    structure.depthMeters = clamp(number(data.depthMeters, 3), 0.2, 200);
  }
  if (data.heightMeters !== undefined) {
    structure.heightMeters = clamp(number(data.heightMeters, 2.6), 0, 60);
  }
  if (ROOF_SHAPES.includes(data.roof as RoofShape)) structure.roof = data.roof as RoofShape;
  if (data.roofRiseMeters !== undefined) {
    structure.roofRiseMeters = clamp(number(data.roofRiseMeters, 1.2), 0, 30);
  }
  if (PAVING_MATERIALS.includes(data.paving as PavingMaterial)) {
    structure.paving = data.paving as PavingMaterial;
  }
  if (data.clearanceMeters !== undefined) {
    structure.clearanceMeters = clamp(number(data.clearanceMeters, 0), 0, 20);
  }

  const wall = hexColor(data.wallColor);
  if (wall) structure.wallColor = wall;
  const roofColor = hexColor(data.roofColor);
  if (roofColor) structure.roofColor = roofColor;
  const regionId = text(data.regionId);
  if (regionId) structure.regionId = regionId;
  const sitsOn = text(data.sitsOn);
  if (sitsOn) structure.sitsOn = sitsOn;
  const notes = text(data.notes);
  if (notes) structure.notes = notes;

  return structure;
}

/**
 * Read a polygon in either `[[x, z], ...]` or `[{x, z}, ...]` form, clamped to a range.
 *
 * Both forms are accepted because both keep turning up: the array form is what a model reaches for
 * when it is emitting many vertices and counting tokens, the object form is what it produces when
 * the surrounding schema uses named fields.
 *
 * A polygon with fewer than three vertices comes back as an empty list rather than throwing. One
 * malformed outline among forty is a value problem, and every caller already checks the length and
 * reports what it dropped — failing the whole stage would throw away four good regions to punish a
 * fifth, and cost a paid call to re-earn them.
 */
function parsePolygon(raw: unknown, min: number, max: number): [number, number][] {
  if (!Array.isArray(raw)) return [];
  const points: [number, number][] = [];
  for (const entry of raw) {
    if (Array.isArray(entry) && entry.length >= 2) {
      points.push([clamp(number(entry[0], 0), min, max), clamp(number(entry[1], 0), min, max)]);
    } else if (entry && typeof entry === 'object') {
      const point = entry as Record<string, unknown>;
      const a = point.x ?? point.u;
      const b = point.z ?? point.v ?? point.y;
      points.push([clamp(number(a, 0), min, max), clamp(number(b, 0), min, max)]);
    }
  }
  return points.length >= 3 ? points : [];
}

/* --------------------------------- Helpers --------------------------------- */

/**
 * Read one planting mass. Kept in this module rather than imported from `SitePlan` because that
 * module's parser is private to it; the shape is the shared contract, not the reader.
 */
function parsePlantingEntry(raw: unknown, index: number): PlanPlanting {
  const where = `flora.plantings[${index}]`;
  const data = asRecord(raw, where);

  const speciesId = text(data.speciesId);
  if (!speciesId) throw new SitePlanFormatError(`${where} is missing a speciesId.`);

  const maturity = Array.isArray(data.maturity) ? data.maturity : [];
  const low = clamp(number(maturity[0], 0.6), 0, 1.5);
  const high = clamp(number(maturity[1], low), low, 1.5);

  const planting: PlanPlanting = {
    id: text(data.id) ?? `planting-${index + 1}`,
    name: text(data.name) ?? speciesId,
    speciesId,
    layer: text(data.layer) ?? 'default',
    minSpacingMeters: clamp(number(data.minSpacingMeters, 1), 0.05, 50),
    maturity: [low, high],
  };

  const group = text(data.group);
  if (group) planting.group = group;
  if (data.linked === true) planting.linked = true;
  const pruning = text(data.pruningStyle);
  if (pruning) planting.pruningStyle = pruning;

  // A hand-drawn bed is not a rectangle, and the shape is the reason it was drawn. Parsed before the
  // other forms because it constrains them rather than competing with them — see `PlanPlanting`.
  const boundary = parsePolygon(data.polygon, -1000, 1000);
  if (boundary.length >= 3) planting.polygon = boundary;

  if (Array.isArray(data.regions) && data.regions.length > 0) {
    planting.regions = data.regions.map((entry, i) => {
      const rect = asRecord(entry, `${where}.regions[${i}]`);
      const xa = number(rect.x0, 0);
      const xb = number(rect.x1, 0);
      const za = number(rect.z0, 0);
      const zb = number(rect.z1, 0);
      return {
        x0: Math.min(xa, xb),
        x1: Math.max(xa, xb),
        z0: Math.min(za, zb),
        z1: Math.max(za, zb),
      };
    });
    planting.count = clamp(Math.trunc(number(data.count, 10)), 0, 2000);
  } else if (planting.polygon && data.count !== undefined) {
    // A boundary on its own is a shape to fill, and `count` is how full.
    planting.count = clamp(Math.trunc(number(data.count, 10)), 0, 2000);
  }

  if (Array.isArray(data.drifts) && data.drifts.length > 0) {
    planting.drifts = data.drifts.map((entry, i) => {
      const drift = asRecord(entry, `${where}.drifts[${i}]`);
      return {
        x: number(drift.x, 0),
        z: number(drift.z, 0),
        radiusMeters: clamp(number(drift.radiusMeters, 1.5), 0.1, 40),
        count: clamp(Math.trunc(number(drift.count, 5)), 0, 500),
      };
    });
  }

  if (data.line !== undefined && data.line !== null) {
    const line = asRecord(data.line, `${where}.line`);
    const points: [number, number][] = [];
    for (const entry of asArray(line.points, `${where}.line.points`)) {
      if (Array.isArray(entry) && entry.length >= 2) {
        points.push([number(entry[0], 0), number(entry[1], 0)]);
      } else if (entry && typeof entry === 'object') {
        const point = entry as Record<string, unknown>;
        points.push([number(point.x, 0), number(point.z, 0)]);
      }
    }
    if (points.length < 2) {
      throw new SitePlanFormatError(`${where}.line needs at least two points.`);
    }
    planting.line = {
      points,
      spacingMeters: clamp(number(line.spacingMeters, 1.5), 0.1, 50),
    };
    const offset = number(line.offsetMeters, 0);
    if (offset !== 0) planting.line.offsetMeters = clamp(offset, -50, 50);
    const jitter = number(line.jitterMeters, 0);
    if (jitter !== 0) planting.line.jitterMeters = clamp(jitter, 0, 10);
  }

  return planting;
}

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new SitePlanFormatError(`Expected ${where} to be an object.`);
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) throw new SitePlanFormatError(`Expected ${where} to be a list.`);
  return value;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function number(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wrap(value: number, range: number): number {
  return ((value % range) + range) % range;
}

function hexColor(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const [r, g, b] = [trimmed[1], trimmed[2], trimmed[3]];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return undefined;
}
